import React from 'react';
import { motion } from 'framer-motion';

interface ErrorCirclesProps {
  centerX: number;
  centerY: number;
  errors: number;
}

const ErrorCircles: React.FC<ErrorCirclesProps> = ({ centerX, centerY, errors }) => {
  const centerRadius = 50; // Larger center circle
  const ringWidth = 12; // Thicker rings
  
  // Color progression: center purple → 1st error blue → 2nd error green → 3rd error yellow → 4th error red
  const getCircleColor = (circleIndex: number) => {
    const colors = [
      '#8b5cf6', // purple-500 - center circle (0 errors)
      '#3b82f6', // blue-500 - first error ring
      '#10b981', // emerald-500 - second error ring
      '#f59e0b', // amber-500 - third error ring (yellow)
      '#ef4444', // red-500 - fourth error ring
    ];
    return colors[circleIndex] || '#ef4444';
  };

  return (
    <g className="error-circles">
      {/* Center circle gradient definitions */}
      <defs>
        <radialGradient id="centerGradient" cx="0.3" cy="0.3">
          <stop offset="0%" stopColor="#a855f7" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </radialGradient>
        
        {/* Error ring gradients */}
        {Array.from({ length: 4 }, (_, i) => (
          <radialGradient key={`errorGradient${i}`} id={`errorGradient${i}`} cx="0.3" cy="0.3">
            <stop offset="0%" stopColor={`${getCircleColor(i + 1)}40`} />
            <stop offset="100%" stopColor={getCircleColor(i + 1)} />
          </radialGradient>
        ))}
      </defs>

      {/* Render error rings from largest to smallest (behind center) */}
      {Array.from({ length: errors }, (_, i) => {
        const ringIndex = errors - 1 - i; // Reverse order so largest rings render first
        const radius = centerRadius + (ringIndex + 1) * ringWidth;
        
        return (
          <motion.circle
            key={`ring-${ringIndex}`}
            cx={centerX}
            cy={centerY}
            r={radius}
            fill={`url(#errorGradient${ringIndex})`}
            stroke={getCircleColor(ringIndex + 1)}
            strokeWidth={2}
            opacity={0.7}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.7 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
              delay: 0.3 + (errors - 1 - ringIndex) * 0.15,
            }}
          />
        );
      })}

      {/* Center circle (always on top with purple gradient) */}
      <motion.circle
        cx={centerX}
        cy={centerY}
        r={centerRadius}
        fill="url(#centerGradient)"
        stroke="#8b5cf6"
        strokeWidth={3}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ 
          type: "spring",
          stiffness: 300,
          damping: 20,
          delay: 0.1,
        }}
      />

      {/* Subtle pulsing effect on center circle */}
      <motion.circle
        cx={centerX}
        cy={centerY}
        r={centerRadius - 2}
        fill="rgba(139, 92, 246, 0.3)"
        initial={{ scale: 1, opacity: 0.5 }}
        animate={{ 
          scale: [1, 1.05, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </g>
  );
};

export default ErrorCircles;